import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getOrganizationAiIntegrationUsageSnapshot } from '../apis/organizations'
import {
  persistBackfillResult,
  JOB_MAX_SECONDS
} from './useConfirmBackfillResult'
import {
  classifyBackfillOutcome,
  backfillChanged,
  type BackfillOutcomeResult
} from '../utils/aiBackfillOutcome'
import { queryKeys } from '../lib/queryKeys'
import { JWT } from '../constants/local_storage'
import { ENV_STORAGE_SUFFIX } from '../constants/apis'
import type { OrgAiIntegrationUsage, AiBackfillTriggerResponse } from '../types'

// No pollable status record, so completion is inferred: poll /usage and finalize
// when data changes vs the before-snapshot, or as "not confirmed yet" when cap_seconds
// elapses; those keep polling until the data moves or JOB_MAX_SECONDS passes.
export const POLL_MS = 10000
// sessionStorage so an in-flight backfill survives navigation within the tab.
const STORAGE_KEY = `ai_backfills_v1${ENV_STORAGE_SUFFIX}`

export interface BackfillJob {
  id: string
  orgId: string | number
  orgName?: string
  orgWebsite?: string | null
  saId: number
  tool: string // provider display name, e.g. "Copilot"
  method?: string // auth/collection method, e.g. "Classic token"
  connectedAt?: string | null // SA connected date — drives the token-age display
  since: string
  until: string
  before: OrgAiIntegrationUsage
  estimateLabel: string
  capSeconds: number
  startedAt: number // epoch ms
  state: 'running' | 'done'
  durationSeconds?: number // wall-clock the backfill took (set on completion)
  after?: OrgAiIntegrationUsage
  outcome?: BackfillOutcomeResult
}

export interface StartBackfillArgs {
  trigger: AiBackfillTriggerResponse
  orgId: string | number
  orgName?: string
  orgWebsite?: string | null
  saId: number
  tool: string
  method?: string
  connectedAt?: string | null
  since: string
  until: string
}

interface AiBackfillContextValue {
  jobs: BackfillJob[]
  start: (args: StartBackfillArgs) => void
  dismiss: (id: string) => void
}

const AiBackfillContext = createContext<AiBackfillContextValue | null>(null)

export const useAiBackfill = (): AiBackfillContextValue => {
  const ctx = useContext(AiBackfillContext)
  if (!ctx) {
    throw new Error('useAiBackfill must be used within an AiBackfillProvider')
  }
  return ctx
}

// Timed out with nothing moved yet: the dock keeps polling it in the background.
export const isConfirming = (job: BackfillJob): boolean =>
  job.state === 'done' && job.outcome?.outcome === 'unconfirmed'

const loadJobs = (): BackfillJob[] => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]')
  } catch {
    return []
  }
}

export const AiBackfillProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [jobs, setJobs] = useState<BackfillJob[]>(loadJobs)
  const queryClient = useQueryClient()
  // Mirror into a ref so the poll interval reads current jobs without resubscribing.
  const jobsRef = useRef(jobs)
  jobsRef.current = jobs

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(jobs))
    } catch {
      /* sessionStorage full / unavailable — non-fatal */
    }
  }, [jobs])

  const start = useCallback((a: StartBackfillArgs) => {
    const job: BackfillJob = {
      id: a.trigger.jobs_suite_uuid,
      orgId: a.orgId,
      orgName: a.orgName,
      orgWebsite: a.orgWebsite,
      saId: a.saId,
      tool: a.tool,
      method: a.method,
      connectedAt: a.connectedAt,
      since: a.since,
      until: a.until,
      before: a.trigger.before,
      estimateLabel: a.trigger.estimate.label,
      capSeconds: a.trigger.estimate.cap_seconds,
      startedAt: Date.now(),
      state: 'running'
    }
    setJobs(prev => [...prev.filter(j => j.id !== job.id), job])
  }, [])

  const dismiss = useCallback((id: string) => {
    setJobs(prev => prev.filter(j => j.id !== id))
  }, [])

  useEffect(() => {
    // A job can now be saved twice (unconfirmed, then settled): no tick starts
    // while the previous one's saves are in flight, so a stale write can't land last.
    let ticking = false
    const tick = async (): Promise<void> => {
      // Stop polling once logged out / JWT gone, so we don't spam 401s every poll.
      if (ticking || !localStorage.getItem(JWT)) return
      const pending = jobsRef.current.filter(
        j => j.state === 'running' || isConfirming(j)
      )
      if (pending.length === 0) return
      ticking = true
      try {
        await pollJobs(pending)
      } finally {
        ticking = false
      }
    }
    const pollJobs = async (pending: BackfillJob[]): Promise<void> => {
      const results = await Promise.all(
        pending.map(async job => {
          try {
            const { data: after } =
              await getOrganizationAiIntegrationUsageSnapshot(
                job.orgId,
                job.saId,
                job.since,
                job.until
              )
            const elapsed = (Date.now() - job.startedAt) / 1000
            const changed = backfillChanged(job.before, after)
            const confirming = isConfirming(job)
            const settleAt = confirming ? JOB_MAX_SECONDS : job.capSeconds
            if (changed || elapsed >= settleAt) {
              return {
                id: job.id,
                after,
                outcome: classifyBackfillOutcome(
                  job.before,
                  after,
                  !changed && elapsed < JOB_MAX_SECONDS
                ),
                // A late confirmation keeps the run time it was first reported with.
                durationSeconds: confirming
                  ? (job.durationSeconds ?? elapsed)
                  : elapsed
              }
            }
          } catch {
            /* transient error — keep polling on the next tick */
          }
          return null
        })
      )
      const done = results.filter((r): r is NonNullable<typeof r> => r !== null)
      if (done.length > 0) {
        // Persist each finished record to S3 (drawer reloads it later), then
        // refresh the history query so the new run shows without a page reload.
        // A save failure is swallowed and must not block the UI marking the job
        // done below; the saves are awaited only after that.
        const saves = done.map(async u => {
          const job = pending.find(j => j.id === u.id)
          if (!job) return
          // Outside the save: a successful backfill clears the sensor's
          // failing_reason (and moves its cost/models), so the cards must
          // refresh even when the result save fails.
          queryClient.invalidateQueries({
            queryKey: queryKeys.organizations.aiIntegrations(job.orgId)
          })
          try {
            await persistBackfillResult(
              queryClient,
              job.orgId,
              job.saId,
              job.id,
              {
                since: job.since,
                until: job.until,
                tool: job.tool,
                method: job.method,
                before: job.before,
                after: u.after,
                outcome: u.outcome,
                duration_seconds: u.durationSeconds
              }
            )
          } catch {
            /* storage failure must not break the flow */
          }
        })
        setJobs(prev =>
          prev.map(j => {
            const u = done.find(d => d.id === j.id)
            return u
              ? {
                  ...j,
                  state: 'done' as const,
                  after: u.after,
                  outcome: u.outcome,
                  durationSeconds: u.durationSeconds
                }
              : j
          })
        )
        await Promise.all(saves)
      }
    }
    const interval = setInterval(tick, POLL_MS)
    return () => clearInterval(interval)
  }, [])

  // Memoize so consumers don't re-render on unrelated App re-renders.
  const value = useMemo(
    () => ({ jobs, start, dismiss }),
    [jobs, start, dismiss]
  )

  return (
    <AiBackfillContext.Provider value={value}>
      {children}
    </AiBackfillContext.Provider>
  )
}
